import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Award, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { DesignationService } from '../services/designation.service';

const COLUMNS = [
  { header: 'Designation Name', accessor: 'name' },
  { header: 'Description', accessor: 'description' },
  { header: 'Status', accessor: 'status' },
];

const FORM_FIELDS = [
  { label: 'Designation Name', accessor: 'name', placeholder: 'e.g. President, Secretary' },
  { label: 'Designation order', accessor: 'order', placeholder: 'e.g. 1, 2, 3' },
  {
    label: 'Status', accessor: 'status', type: 'select', options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]
  },
  {
    label: 'Description',
    accessor: 'description',
    type: 'textarea',
    placeholder: 'Describe the responsibilities of this designation...',
    fullWidth: true,
  },
];

export default function Designations() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 8;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await DesignationService.getAllDesignations();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formatted = list.map(item => ({ ...item, id: item._id || item.id }));
      setDataList(formatted);
    } catch (error) {
      toast.error('Failed to load designations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!debouncedQuery) return dataList;
    return dataList.filter(item =>
      Object.values(item).some(val =>
        String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
      )
    );
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    const active = dataList.filter(d => d.status === 'active').length;
    return [
      { label: 'Total Designations', value: total, icon: <Award size={20} />, color: 'blue' },
      { label: 'Active', value: active, icon: <CheckCircle size={20} />, color: 'green' },
      { label: 'Inactive', value: total - active, icon: <XCircle size={20} />, color: 'orange' },
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    try {
      if (editingData) {
        const targetId = editingData._id || editingData.id;
        await DesignationService.updateDesignation(targetId, formData);
        toast.success('Designation updated successfully!');
      } else {
        await DesignationService.createDesignation(formData);
        toast.success('Designation created successfully!');
      }
      await fetchData();
      setIsFormOpen(false);
      setEditingData(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save designation');
    }
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await DesignationService.deleteDesignation(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Designation deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete designation');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Designations"
        subtitle="Manage official titles and designations for committee members"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Designation"
        kpis={kpis}
      />

      {isFormOpen && (
        <DataForm
          title={editingData ? 'Edit Designation' : 'Add New Designation'}
          fields={FORM_FIELDS}
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}

      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search designations..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Designation"
        message="Are you sure you want to delete this designation? Committee members assigned to this might be affected."
        itemName={deleteTarget ? deleteTarget.name : ''}
      />
    </div>
  );
}
